import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { HashLink } from "react-router-hash-link";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../context/AuthContext";
import { useNavigationLock } from "../../context/NavigationLockContext";
import ConfirmationModal from "../admin/ConfirmationModal";
import { isUsabilityTestsEnabled } from "../../config/featureFlags";

function HeaderTailwind() {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, user, logout } = useAuth();
  const { isNavigationLocked, lockMessage } = useNavigationLock();
  const [navLockToast, setNavLockToast] = useState(false);
  const navLockToastTimeout = useRef(null);
  const [dropdowns, setDropdowns] = useState({
    event: false,
    about: false,
    results: false,
    adminDashboard: false,
    adminManagement: false,
    adminOrganization: false,
    adminResults: false,
    adminUsers: false,
    adminUsability: false
  });
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [darkMode, setDarkMode] = useState(false);

  // Initialisiere lang direkt aus URL oder i18n, um Flackern zu vermeiden
  const getInitialLang = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const urlLang = urlParams.get("lang");
    const i18nLang = i18n.language?.split('-')[0] || i18n.language || "de";
    return urlLang || i18nLang || "de";
  };
  const [lang, setLang] = useState(getInitialLang);

  const dropdownRef = useRef(null);
  const [profileOpen, setProfileOpen] = useState(false);
  const [logoutConfirmOpen, setLogoutConfirmOpen] = useState(false);
  const profileRef = useRef(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [headerVisible, setHeaderVisible] = useState(true);

  useEffect(() => {
    const isDark = localStorage.getItem("darkMode") === "true";
    setDarkMode(isDark);
    if (isDark) {
      document.documentElement.classList.add('dark');
      document.body.classList.add('dark-mode');
    } else {
      document.documentElement.classList.remove('dark');
      document.body.classList.remove('dark-mode');
    }
  }, []);

  // Synchronisiere lang State mit i18n.language und location (ohne Flackern)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const urlLang = urlParams.get("lang");
    const i18nLang = i18n.language?.split('-')[0] || i18n.language || "de";

    // Priorität: URL-Parameter > i18n.language > "de"
    const currentLang = urlLang || i18nLang || "de";

    if (currentLang !== lang) {
      setLang(currentLang);
    }
  }, [location.search, i18n.language, lang]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      // Ignoriere Klicks auf Links - diese sollen die Navigation nicht verhindern
      if (event.target.closest('a')) {
        return;
      }
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdowns({
          event: false,
          about: false,
          results: false,
          adminDashboard: false,
          adminManagement: false,
          adminOrganization: false,
          adminResults: false,
          adminUsers: false,
          adminUsability: false
        });
      }
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setProfileOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Schließe Mobile-Menu und Dropdowns wenn sich die Route ändert
  useEffect(() => {
    setMobileMenuOpen(false);
    setDropdowns({
      event: false,
      about: false,
      results: false,
      adminDashboard: false,
      adminManagement: false,
      adminOrganization: false,
      adminResults: false,
      adminUsers: false,
      adminUsability: false
    });
  }, [location.pathname]);

  // Scroll-Verhalten für Header (nur auf Mobile)
  useEffect(() => {
    const handleScroll = () => {
      // Nur auf Mobile aktivieren
      if (window.innerWidth >= 768) {
        setHeaderVisible(true);
        return;
      }

      const currentScrollY = window.scrollY;

      // Am oberen Rand immer sichtbar
      if (currentScrollY < 10) {
        setHeaderVisible(true);
        setIsScrolled(false);
      } else {
        setIsScrolled(true);
        // Beim Scrollen nach unten verstecken, beim Scrollen nach oben zeigen
        if (currentScrollY > lastScrollY && currentScrollY > 100) {
          setHeaderVisible(false);
        } else if (currentScrollY < lastScrollY) {
          setHeaderVisible(true);
        }
      }

      setLastScrollY(currentScrollY);
    };

    const handleResize = () => {
      // Bei Größenänderung Header wieder anzeigen
      if (window.innerWidth >= 768) {
        setHeaderVisible(true);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
    };
  }, [lastScrollY]);

  const isActive = (path) => {
    if (path === "/") {
      return location.pathname === path;
    }
    return location.pathname.startsWith(path);
  };

  const toggleDropdown = (dropdown) => {
    setDropdowns(prev => ({
      event: dropdown === "event" ? !prev.event : false,
      about: dropdown === "about" ? !prev.about : false,
      results: dropdown === "results" ? !prev.results : false,
      adminDashboard: dropdown === "adminDashboard" ? !prev.adminDashboard : false,
      adminManagement: dropdown === "adminManagement" ? !prev.adminManagement : false,
      adminOrganization: dropdown === "adminOrganization" ? !prev.adminOrganization : false,
      adminResults: dropdown === "adminResults" ? !prev.adminResults : false,
      adminUsers: dropdown === "adminUsers" ? !prev.adminUsers : false,
      adminUsability: dropdown === "adminUsability" ? !prev.adminUsability : false
    }));
  };

  const toggleDarkMode = () => {
    const newDarkMode = !darkMode;
    setDarkMode(newDarkMode);
    localStorage.setItem("darkMode", newDarkMode);
    // Tailwind erwartet 'dark' Klasse, nicht 'dark-mode'
    if (newDarkMode) {
      document.documentElement.classList.add('dark');
      document.body.classList.add('dark-mode'); // Für CSS-Variablen
    } else {
      document.documentElement.classList.remove('dark');
      document.body.classList.remove('dark-mode');
    }
  };

  // Zeige Toast-Benachrichtigung wenn Navigation gesperrt ist
  const showNavLockToast = useCallback(() => {
    setNavLockToast(true);
    if (navLockToastTimeout.current) clearTimeout(navLockToastTimeout.current);
    navLockToastTimeout.current = setTimeout(() => setNavLockToast(false), 3000);
  }, []);

  // Cleanup Toast-Timeout beim Unmount
  useEffect(() => {
    return () => {
      if (navLockToastTimeout.current) clearTimeout(navLockToastTimeout.current);
    };
  }, []);

  // Handler der Navigation blockiert wenn Lock aktiv ist
  const handleLockedNavigation = useCallback((e) => {
    if (isNavigationLocked) {
      e.preventDefault();
      showNavLockToast();
      return true; // Blocked
    }
    return false; // Not blocked
  }, [isNavigationLocked, showNavLockToast]);

  const handleLogout = () => {
    logout();
    setMobileMenuOpen(false);
    setProfileOpen(false);
    setLogoutConfirmOpen(false);
    navigate("/admin", { state: { loggedOut: true } });
  };

  const changeLanguage = (langCode) => {
    setLang(langCode);
    const url = new URL(window.location);
    url.searchParams.set("lang", langCode);
    window.history.pushState({}, "", url);
    i18n.changeLanguage(langCode);
  };

  const scrollToTop = (e) => {
    // Verhindere nicht die Standard-Navigation
    setMobileMenuOpen(false);
    setProfileOpen(false);
    // Schließe alle Dropdowns
    setDropdowns({
      event: false,
      about: false,
      results: false,
      adminDashboard: false,
      adminManagement: false,
      adminOrganization: false,
      adminResults: false,
      adminUsers: false,
      adminUsability: false
    });
    // Scroll nur wenn wir auf derselben Seite bleiben
    if (location.pathname === window.location.pathname) {
      window.scrollTo(0, 0);
    }
  };

  const handleHashClick = (e) => {
    setMobileMenuOpen(false);
    // HashLink kümmert sich selbst um die Navigation
  };

  // Verwende immer den Admin Header für eingeloggte User, unabhängig von der Rolle
  const isAdmin = isAuthenticated;

  // Permission-Map für Menüpunkte
  const menuPermissions = {
    "/admin/dashboard": ["view_dashboard"],
    "/admin/participants": ["manage_participants"],
    "/admin/nations": ["manage_nations"],
    "/admin/categories": ["manage_categories"],
    "/admin/groups": ["manage_groups"],
    "/admin/schedule": ["manage_schedule"],
    "/admin/results": ["view_results", "manage_results"],
    "/admin/medal-table": ["view_results"],
    "/admin/mvp-table": ["view_results"],
    "/admin/users": ["manage_users"],
    "/admin/user-roles": ["manage_roles"],
    "/admin/schedule-settings": ["manage_schedule_settings"],
    "/admin/data-management": ["manage_data"]
  };

  // Prüft, ob der User die benötigten Permissions hat
  const hasPermission = (path) => {
    if (!isAuthenticated || !user) return false;

    const normalizeRole = (role) => {
      if (!role) return '';
      return role.toLowerCase().replace(/[-_]/g, '');
    };

    const userRole = normalizeRole(user?.role);
    // Superadmin hat automatisch alle Permissions
    if (userRole === 'superadmin') return true;

    const requiredPermissions = menuPermissions[path];
    if (!requiredPermissions || requiredPermissions.length === 0) return true;

    const userPermissions = Array.isArray(user?.permissions) ? user.permissions : [];
    return requiredPermissions.some((permission) => userPermissions.includes(permission));
  };

  // Filtert Menüpunkte basierend auf Permissions und show-Eigenschaft
  const filterMenuItems = (items) => {
    return items.filter((item) => {
      // Prüfe explizite show-Eigenschaft
      if (item.show === false) {
        return false;
      }

      if (item.type === "link" && item.to) {
        // Prüfe Permissions nur wenn show nicht explizit auf true gesetzt ist
        if (item.show === true) {
          return true;
        }
        return hasPermission(item.to);
      }
      if (item.type === "dropdown" && item.items) {
        const filteredItems = filterMenuItems(item.items);
        // Dropdown nur anzeigen, wenn mindestens ein Item sichtbar ist
        return filteredItems.length > 0;
      }
      return true;
    }).map((item) => {
      if (item.type === "dropdown" && item.items) {
        return {
          ...item,
          items: filterMenuItems(item.items)
        };
      }
      return item;
    });
  };

  const adminMenu = [
    {
      type: "link",
      to: "/admin/dashboard",
      icon: "fa-tachometer-alt",
      label: t("admin.menu.dashboard", "Dashboard"),
      isActive: isActive("/admin/dashboard"),
      onClick: scrollToTop
    },
    {
      type: "dropdown",
      label: t("admin.menu.management", "Verwaltung"),
      icon: "fa-cogs",
      isActive: isActive("/admin/participants") || isActive("/admin/nations") || isActive("/admin/categories"),
      dropdownKey: "adminManagement",
      open: dropdowns.adminManagement,
      items: [
        {
          type: "link",
          to: "/admin/participants",
          icon: "fa-users",
          label: t("admin.menu.participants", "Teilnehmer"),
          isActive: isActive("/admin/participants"),
          onClick: scrollToTop
        },
        {
          type: "link",
          to: "/admin/nations",
          icon: "fa-flag",
          label: t("admin.menu.nations", "Nationen"),
          isActive: isActive("/admin/nations"),
          onClick: scrollToTop
        },
        {
          type: "link",
          to: "/admin/categories",
          icon: "fa-list",
          label: t("admin.menu.categories", "Kategorien"),
          isActive: isActive("/admin/categories"),
          onClick: scrollToTop
        }
      ]
    },
    {
      type: "dropdown",
      label: t("admin.menu.organization", "Organisation"),
      icon: "fa-calendar-alt",
      isActive: isActive("/admin/groups") || isActive("/admin/schedule") || isActive("/caller") || isActive("/admin/schedule-settings"),
      dropdownKey: "adminOrganization",
      open: dropdowns.adminOrganization,
      items: [
        {
          type: "link",
          to: "/admin/groups",
          icon: "fa-layer-group",
          label: t("admin.menu.groups", "Gruppen"),
          isActive: isActive("/admin/groups"),
          onClick: scrollToTop
        },
        {
          type: "link",
          to: "/admin/schedule",
          icon: "fa-calendar-alt",
          label: t("admin.menu.schedule", "Zeitplan"),
          isActive: isActive("/admin/schedule"),
          onClick: scrollToTop
        },
        {
          type: "link",
          to: "/caller",
          icon: "fa-bullhorn",
          label: t("admin.menu.caller", "Caller"),
          isActive: isActive("/caller"),
          onClick: scrollToTop,
          show: isAuthenticated && user && (user.role === "caller" || user.role === "admin" || user.role === "superadmin")
        },
        {
          type: "link",
          to: "/admin/schedule-settings",
          icon: "fa-sliders-h",
          label: t("admin.menu.settingsSchedule", "Zeitplan-Konfiguration"),
          isActive: location.pathname === "/admin/schedule-settings",
          onClick: scrollToTop,
          show: isAuthenticated && user && (user.role === "admin" || user.role === "superadmin")
        }
      ]
    },
    {
      type: "dropdown",
      label: t("admin.menu.results", "Ergebnisse"),
      icon: "fa-clipboard-list",
      isActive: isActive("/admin/results") || isActive("/admin/medal-table") || isActive("/admin/mvp-table") || isActive("/scorekeeper"),
      dropdownKey: "adminResults",
      open: dropdowns.adminResults,
      show: isAuthenticated && user && user.role !== "caller",
      items: [
        {
          type: "link",
          to: "/admin/results",
          icon: "fa-clipboard-list",
          label: t("admin.menu.results", "Ergebnisse"),
          isActive: isActive("/admin/results"),
          onClick: scrollToTop,
          show: isAuthenticated && user && (user.role === "admin" || user.role === "superadmin")
        },
        {
          type: "link",
          to: "/admin/medal-table",
          icon: "fa-medal",
          label: t("admin.menu.medalTable", "Medaillenspiegel"),
          isActive: isActive("/admin/medal-table"),
          onClick: scrollToTop,
          show: isAuthenticated && user && (user.role === "admin" || user.role === "superadmin")
        },
        {
          type: "link",
          to: "/admin/mvp-table",
          icon: "fa-trophy",
          label: t("admin.menu.mvpTable", "MVP-Tabelle"),
          isActive: isActive("/admin/mvp-table"),
          onClick: scrollToTop,
          show: isAuthenticated && user && (user.role === "admin" || user.role === "superadmin")
        },
        {
          type: "link",
          to: "/scorekeeper",
          icon: "fa-edit",
          label: t("admin.menu.scorekeeper", "Punkteerfassung"),
          isActive: isActive("/scorekeeper"),
          onClick: scrollToTop,
          show: isAuthenticated && user && (user.role === "scorekeeper" || user.role === "admin" || user.role === "superadmin")
        }
      ]
    },
    {
      type: "dropdown",
      label: t("admin.menu.users", "Benutzer"),
      icon: "fa-user-cog",
      isActive: location.pathname.startsWith("/admin/users") || location.pathname.startsWith("/admin/user-roles") || isActive("/admin/data-management"),
      dropdownKey: "adminUsers",
      open: dropdowns.adminUsers,
      show: isAuthenticated && user && user.role === "superadmin",
      items: [
        {
          type: "link",
          to: "/admin/users",
          icon: "fa-users-cog",
          label: t("admin.menu.usersOverview", "Benutzerübersicht"),
          isActive: location.pathname === "/admin/users",
          onClick: scrollToTop
        },
        {
          type: "link",
          to: "/admin/user-roles",
          icon: "fa-key",
          label: t("admin.menu.userRoles", "Nutzerrollen"),
          isActive: location.pathname === "/admin/user-roles",
          onClick: scrollToTop
        },
        {
          type: "link",
          to: "/admin/data-management",
          icon: "fa-cog",
          label: t("admin.menu.settingsData", "Datenmanagement"),
          isActive: location.pathname === "/admin/data-management",
          onClick: scrollToTop,
          show: isAuthenticated && user && (user.role === "admin" || user.role === "superadmin")
        }
      ]
    },
    // Usability-Tests: Für Superadmin als Dropdown mit allen Optionen,
    // für andere Rollen als einfacher Link (nur "Test durchführen")
    ...(isAuthenticated && user && isUsabilityTestsEnabled ? [
      user.role === "superadmin" ? {
        type: "dropdown",
        label: t("admin.menu.usability", "Usability-Tests"),
        icon: "fa-vial",
        isActive: location.pathname.startsWith("/admin/usability") || location.pathname === "/usability-tests",
        dropdownKey: "adminUsability",
        open: dropdowns.adminUsability || false,
        show: true,
        items: [
          {
            type: "link",
            to: "/usability-tests",
            icon: "fa-vial",
            label: t("admin.menu.usabilityWizard", "Usability-Test durchführen"),
            isActive: location.pathname === "/usability-tests",
            onClick: scrollToTop,
            show: true
          },
          {
            type: "link",
            to: "/admin/usability/tasks",
            icon: "fa-tasks",
            label: t("admin.menu.usabilityTasks", "Aufgaben verwalten"),
            isActive: location.pathname === "/admin/usability/tasks",
            onClick: scrollToTop,
            show: true
          },
          {
            type: "link",
            to: "/admin/usability/open-questions",
            icon: "fa-question-circle",
            label: t("admin.menu.usabilityOpenQuestions", "Offene Fragen"),
            isActive: location.pathname === "/admin/usability/open-questions",
            onClick: scrollToTop,
            show: true
          },
          {
            type: "link",
            to: "/admin/usability/evaluations",
            icon: "fa-chart-bar",
            label: t("admin.menu.usabilityEvaluations", "Auswertungen"),
            isActive: location.pathname === "/admin/usability/evaluations",
            onClick: scrollToTop,
            show: true
          },
          {
            type: "link",
            to: "/admin/usability/statistics",
            icon: "fa-chart-line",
            label: t("admin.menu.usabilityStatistics", "Statistiken"),
            isActive: location.pathname === "/admin/usability/statistics",
            onClick: scrollToTop,
            show: true
          }
        ]
      } : {
        type: "link",
        to: "/usability-tests",
        icon: "fa-vial",
        label: t("admin.menu.usability", "Usability-Tests"),
        isActive: location.pathname === "/usability-tests",
        onClick: scrollToTop,
        show: true
      }
    ] : []),
  ];

  const mainMenu = [
    {
      type: "link",
      to: "/",
      icon: "fa-home",
      label: t("header.home"),
      isActive: isActive("/"),
      onClick: scrollToTop
    },
    {
      type: "dropdown",
      label: t("header.event"),
      icon: "fa-calendar-alt",
      isActive: isActive("/taolu") || isActive("/chinwoo") || location.hash === "#schedule" || location.hash === "#location",
      dropdownKey: "event",
      open: dropdowns.event,
      items: [
        {
          type: "link",
          to: "/taolu",
          icon: "fa-fist-raised",
          label: t("header.tournamentFormat"),
          isActive: location.pathname === "/taolu",
          onClick: scrollToTop
        },
        {
          type: "link",
          to: "/chinwoo",
          icon: "fa-dragon",
          label: t("header.kungFuCulture"),
          isActive: location.pathname === "/chinwoo",
          onClick: scrollToTop
        },
        {
          type: "hashlink",
          to: "/#schedule",
          icon: "fa-calendar-alt",
          label: t("header.schedule"),
          isActive: location.hash === "#schedule",
          onClick: handleHashClick
        },
        {
          type: "hashlink",
          to: "/#location",
          icon: "fa-map-marker-alt",
          label: t("header.location"),
          isActive: location.hash === "#location",
          onClick: handleHashClick
        }
      ]
    },
    {
      type: "dropdown",
      label: t("header.information"),
      icon: "fa-info-circle",
      isActive: isActive("/sponsors-partners") || isActive("/sponsors") || isActive("/downloads"),
      dropdownKey: "about",
      open: dropdowns.about,
      items: [
        {
          type: "link",
          to: "/sponsors-partners",
          icon: "fa-handshake",
          label: t("header.sponsorsPartners"),
          isActive: location.pathname === "/sponsors-partners",
          onClick: scrollToTop
        },
        {
          type: "link",
          to: "/sponsors",
          icon: "fa-heart",
          label: t("header.sponsor"),
          isActive: location.pathname === "/sponsors",
          onClick: scrollToTop
        },
        {
          type: "link",
          to: "/downloads",
          icon: "fa-download",
          label: t("downloads.title"),
          isActive: location.pathname === "/downloads",
          onClick: scrollToTop
        }
      ]
    },
    // Ergebnisse-Menüpunkt nur in Development anzeigen
    ...(process.env.NODE_ENV !== 'production' ? [{
      type: "dropdown",
      label: t("header.results", "Wettkampf"),
      icon: "fa-clipboard-list",
      isActive: isActive("/results") || isActive("/medal-table") || isActive("/mvp-table"),
      dropdownKey: "results",
      open: dropdowns.results,
      items: [
        {
          type: "link",
          to: "/results",
          icon: "fa-clipboard-list",
          label: t("header.allResults", "Übersicht"),
          isActive: isActive("/results"),
          onClick: scrollToTop
        },
        {
          type: "link",
          to: "/medal-table",
          icon: "fa-medal",
          label: t("header.medalTable", "Medaillenspiegel"),
          isActive: isActive("/medal-table"),
          onClick: scrollToTop
        },
        {
          type: "link",
          to: "/mvp-table",
          icon: "fa-trophy",
          label: t("header.mvpTable", "MVP-Tabelle"),
          isActive: isActive("/mvp-table"),
          onClick: scrollToTop,
          show: isAuthenticated
        }
      ]
    }] : []),
    {
      type: "hashlink",
      to: "/#contact",
      icon: "fa-envelope",
      label: t("header.contact"),
      isActive: location.hash === "#contact",
      onClick: handleHashClick
    },

    // Usability-Test-Link für nicht eingeloggte User (nur wenn Feature-Flag an)
    ...(isUsabilityTestsEnabled && !isAuthenticated ? [{
      type: "link",
      to: "/usability-tests",
      icon: "fa-vial",
      label: "Usability-Test",
      isActive: location.pathname === "/usability-tests",
      onClick: scrollToTop,
      show: true // Immer anzeigen, keine Permission-Prüfung
    }] : [])
  ];

  const filteredAdminMenu = isAdmin && isAuthenticated ? filterMenuItems(adminMenu) : adminMenu;
  const menuItems = isAdmin ? filteredAdminMenu : mainMenu;

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 border-b border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-sm transition-all duration-300 md:translate-y-0 ${headerVisible ? 'translate-y-0' : '-translate-y-full'
      } ${isScrolled ? 'shadow-md' : ''}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to={isNavigationLocked ? "#" : "/"} onClick={(e) => {
            if (handleLockedNavigation(e)) return;
            scrollToTop(e);
          }} aria-label={t("header.home", "Startseite")} className="flex items-center gap-2.5 rounded-xl focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/30 dark:focus-visible:ring-primary-light/30 focus-visible:ring-offset-2 focus-visible:ring-offset-white dark:focus-visible:ring-offset-slate-900">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-200/80 dark:border-slate-700 overflow-hidden" aria-hidden>
              <img
                src="/images/logo.png"
                alt=""
                className="h-9 w-9 object-contain"
              />
            </span>
            <span
              className="text-lg sm:text-xl font-bold tracking-tight bg-gradient-to-r from-black via-red-600 to-yellow-500 bg-clip-text text-transparent"
              style={{
                backgroundImage: 'linear-gradient(to right,rgb(0, 0, 0) 0%,rgb(0, 0, 0) 33%, #DC2626 33%, #DC2626 66%, #EAB308 66%, #EAB308 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                // Sehr feiner weißer Umriss pro Zeichen; Verlauf bleibt sichtbar (Strich unter der Füllung)
                WebkitTextStroke: '0.35px rgba(255, 255, 255, 0.5)',
                paintOrder: 'stroke fill'
              }}
            >
              BER26
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden header-break:flex items-center gap-0.5" ref={dropdownRef} aria-label="Hauptnavigation">
            {menuItems.filter(item => item.show !== false).map((item, idx) => (
              <div key={idx} className="relative">
                {item.type === "link" && (
                  <Link
                    to={isNavigationLocked ? "#" : item.to}
                    onClick={(e) => {
                      if (handleLockedNavigation(e)) return;
                      if (item.onClick) item.onClick(e);
                    }}
                    className={`flex items-center gap-2 h-10 px-3.5 rounded-xl text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/25 dark:focus-visible:ring-primary-light/25 ${isNavigationLocked ? 'opacity-50 cursor-not-allowed' : ''
                      } ${item.isActive
                        ? 'bg-primary dark:bg-primary-light text-white shadow-sm'
                        : 'text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800/90'
                      }`}
                  >
                    {isNavigationLocked && <i className="fas fa-lock text-[10px] opacity-60"></i>}
                    <i className={`fas ${item.icon} text-[13px] w-[1em] text-center`}></i>
                    <span className="whitespace-nowrap">{item.label}</span>
                  </Link>
                )}
                {item.type === "hashlink" && (
                  <HashLink
                    to={isNavigationLocked ? "#" : item.to}
                    onClick={(e) => {
                      if (handleLockedNavigation(e)) return;
                      if (item.onClick) item.onClick(e);
                    }}
                    className={`flex items-center gap-2 h-10 px-3.5 rounded-xl text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/25 dark:focus-visible:ring-primary-light/25 ${isNavigationLocked ? 'opacity-50 cursor-not-allowed' : ''
                      } ${item.isActive
                        ? 'bg-primary dark:bg-primary-light text-white shadow-sm'
                        : 'text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800/90'
                      }`}
                  >
                    {isNavigationLocked && <i className="fas fa-lock text-[10px] opacity-60"></i>}
                    <i className={`fas ${item.icon} text-[13px] w-[1em] text-center`}></i>
                    <span className="whitespace-nowrap">{item.label}</span>
                  </HashLink>
                )}
                {item.type === "dropdown" && (
                  <div className="relative">
                    <button
                      type="button"
                      aria-expanded={item.open}
                      aria-haspopup="true"
                      onClick={(e) => {
                        if (isNavigationLocked) {
                          showNavLockToast();
                          return;
                        }
                        toggleDropdown(item.dropdownKey);
                      }}
                      className={`flex items-center gap-2 h-10 px-3.5 rounded-xl text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/25 dark:focus-visible:ring-primary-light/25 ${isNavigationLocked ? 'opacity-50 cursor-not-allowed' : ''
                        } ${item.isActive
                          ? 'bg-primary dark:bg-primary-light text-white shadow-sm'
                          : 'text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800/90'
                        }`}
                    >
                      {isNavigationLocked && <i className="fas fa-lock text-[10px] opacity-60"></i>}
                      <i className={`fas ${item.icon} text-[13px] w-[1em] text-center`}></i>
                      <span className="whitespace-nowrap">{item.label}</span>
                      <i className={`fas fa-chevron-${item.open ? "up" : "down"} text-[10px] opacity-80`}></i>
                    </button>
                    {item.open && !isNavigationLocked && (
                      <div className="absolute top-full left-0 mt-1.5 min-w-[14rem] w-max max-w-[min(18rem,calc(100vw-2rem))] max-h-[min(24rem,calc(100vh-5rem))] overflow-y-auto bg-white dark:bg-slate-800 rounded-xl shadow-lg border border-slate-200 dark:border-slate-700 py-1.5 z-[100]">
                        {item.items.filter(subItem => subItem.show !== false).map((subItem, subIdx) => (
                          <div key={subIdx} className="px-1.5">
                            {subItem.type === "link" && (
                              <Link
                                to={subItem.to}
                                onClick={subItem.onClick}
                                className={`flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-colors ${subItem.isActive
                                  ? 'bg-primary/8 dark:bg-primary-light/10 text-primary dark:text-primary-light font-semibold'
                                  : 'text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700/50'
                                  }`}
                              >
                                <i className={`fas ${subItem.icon} text-[13px] w-4 text-center ${subItem.isActive ? 'text-primary dark:text-primary-light' : 'text-slate-400 dark:text-slate-500'}`}></i>
                                <span className="truncate">{subItem.label}</span>
                              </Link>
                            )}
                            {subItem.type === "hashlink" && (
                              <HashLink
                                to={subItem.to}
                                onClick={subItem.onClick}
                                className={`flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-colors ${subItem.isActive
                                  ? 'bg-primary/8 dark:bg-primary-light/10 text-primary dark:text-primary-light font-semibold'
                                  : 'text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700/50'
                                  }`}
                              >
                                <i className={`fas ${subItem.icon} text-[13px] w-4 text-center ${subItem.isActive ? 'text-primary dark:text-primary-light' : 'text-slate-400 dark:text-slate-500'}`}></i>
                                <span className="truncate">{subItem.label}</span>
                              </HashLink>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </nav>

          {/* Controls */}
          <div className="hidden header-break:flex items-center gap-2 sm:gap-3">
            {/* Spenden Button - nur für nicht eingeloggte Benutzer */}
            {!isAuthenticated && (
              <HashLink
                to={isNavigationLocked ? "#" : "/sponsors#private-donation"}
                onClick={(e) => {
                  if (handleLockedNavigation(e)) return;
                  handleHashClick(e);
                }}
                className={`flex items-center gap-2 h-10 px-4 bg-primary dark:bg-primary-light text-white rounded-xl text-sm font-semibold transition-all hover:bg-primary-dark dark:hover:bg-primary shadow-sm hover:shadow focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/30 dark:focus-visible:ring-primary-light/30 ${isNavigationLocked ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <i className="fas fa-heart text-[11px]"></i>
                <span className="whitespace-nowrap">{t("header.donate", "Spenden")}</span>
              </HashLink>
            )}
            {/* Nur im Admin-Bereich Profile anzeigen (Login-Button entfernt aus Sicherheitsgründen) */}
            {isAdmin && isAuthenticated && (
              <div className="relative" ref={profileRef}>
                <button
                  type="button"
                  aria-expanded={profileOpen}
                  aria-haspopup="menu"
                  onClick={() => setProfileOpen((prev) => !prev)}
                  className={`flex items-center justify-center w-10 h-10 rounded-xl border transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/25 dark:focus-visible:ring-primary-light/25 ${profileOpen ? 'bg-primary dark:bg-primary-light text-white border-primary dark:border-primary-light shadow-md shadow-primary/15' : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700'
                    }`}
                >
                  <i className="fas fa-user text-sm"></i>
                </button>
                {profileOpen && (
                  <div className="absolute right-0 mt-2 w-64 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl shadow-lg overflow-hidden z-[120]" role="menu">
                    <div className="px-4 py-3 bg-slate-50 dark:bg-slate-700/50 border-b border-slate-100 dark:border-slate-700">
                      <div className="text-sm font-semibold text-slate-900 dark:text-slate-100 truncate">
                        {user?.username || t("auth.user", "Nutzer")}
                      </div>
                      {user?.role && (
                        <span className="inline-flex mt-1.5 px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider bg-slate-200/80 dark:bg-slate-600/80 text-slate-700 dark:text-slate-200">
                          {user.role}
                        </span>
                      )}
                    </div>
                    <button
                      type="button"
                      role="menuitem"
                      onClick={() => setLogoutConfirmOpen(true)}
                      className="w-full flex items-center gap-2.5 px-4 py-3 text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700/60 transition-colors"
                    >
                      <span className="w-8 h-8 rounded-lg bg-slate-100 dark:bg-slate-700 flex items-center justify-center">
                        <i className="fas fa-sign-out-alt text-slate-500 dark:text-slate-400 text-xs"></i>
                      </span>
                      <span>{t("header.logout", "Abmelden")}</span>
                    </button>
                  </div>
                )}
              </div>
            )}

            <div className="h-6 w-px bg-slate-200 dark:bg-slate-700 hidden sm:block" aria-hidden />

            {/* Sprache: kompakt — nur kleine Flaggen in einer Gruppe */}
            <div
              className="flex items-center gap-0.5 p-1 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800"
              role="group"
              aria-label={t("header.language", "Sprache")}
            >
              {[
                { code: "de", src: "/images/flags/DE.png", label: "Deutsch" },
                { code: "en", src: "/images/flags/GB.png", label: "English" },
                { code: "zh", src: "/images/flags/CN.png", label: "中文" }
              ].map(({ code, src, label }) => (
                <button
                  key={code}
                  type="button"
                  onClick={() => changeLanguage(code)}
                  aria-pressed={lang === code}
                  aria-label={label}
                  title={label}
                  className={`rounded-lg p-0.5 transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/30 dark:focus-visible:ring-primary-light/30 ${
                    lang === code
                      ? "ring-2 ring-primary/40 dark:ring-primary-light/40 bg-white dark:bg-slate-700 shadow-sm"
                      : "opacity-60 hover:opacity-100"
                  }`}
                >
                  <img src={src} alt="" className="w-6 h-6 object-cover rounded-full" />
                </button>
              ))}
            </div>

            {/* Erscheinungsbild: klassischer Toggle */}
            <button
              type="button"
              onClick={toggleDarkMode}
              aria-label={darkMode ? t("header.themeLight", "Heller Modus") : t("header.themeDark", "Dunkler Modus")}
              title={darkMode ? t("header.themeLight", "Heller Modus") : t("header.themeDark", "Dunkler Modus")}
              className="relative h-8 w-11 shrink-0 rounded-full border border-slate-200 dark:border-slate-600 bg-slate-200 dark:bg-slate-700 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/25 dark:focus-visible:ring-primary-light/25"
            >
              <span
                className={`absolute top-1 left-1 flex h-6 w-6 items-center justify-center rounded-full bg-white dark:bg-slate-800 shadow-sm border border-slate-200 dark:border-slate-600 transition-transform duration-200 ${
                  darkMode ? "translate-x-3" : "translate-x-0"
                }`}
              >
                <i
                  className={`fas text-[10px] ${darkMode ? "fa-moon text-slate-500 dark:text-slate-400" : "fa-sun text-amber-500"}`}
                ></i>
              </span>
            </button>
          </div>

          {/* Mobile Menu Toggle */}
          <button
            type="button"
            aria-expanded={mobileMenuOpen}
            aria-controls="mobile-nav-menu"
            aria-label={mobileMenuOpen ? t("header.closeMenu", "Menü schließen") : t("header.openMenu", "Menü öffnen")}
            onClick={() => {
              if (isNavigationLocked) {
                showNavLockToast();
                return;
              }
              setMobileMenuOpen(!mobileMenuOpen);
            }}
            className={`header-break:hidden flex h-10 w-10 items-center justify-center text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/25 ${isNavigationLocked ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            <i className={`fas ${mobileMenuOpen ? 'fa-times' : 'fa-bars'} text-lg`}></i>
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div id="mobile-nav-menu" className="header-break:hidden pb-4 border-t border-slate-200 dark:border-slate-700 pt-4 max-h-[min(70vh,calc(100dvh-5rem))] overflow-y-auto">
            <nav className="space-y-0.5" aria-label="Mobile Navigation">
              {menuItems.filter(item => item.show !== false).map((item, idx) => (
                <div key={idx}>
                  {item.type === "link" && (
                    <Link
                      to={item.to}
                      onClick={() => setMobileMenuOpen(false)}
                      className={`flex items-center gap-2.5 min-h-11 px-3 rounded-xl text-sm font-medium transition-colors ${item.isActive
                        ? 'bg-primary dark:bg-primary-light text-white shadow-sm'
                        : 'text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800/90'
                        }`}
                    >
                      <i className={`fas ${item.icon} w-4 text-center text-[13px]`}></i>
                      <span>{item.label}</span>
                    </Link>
                  )}
                  {item.type === "hashlink" && (
                    <HashLink
                      to={item.to}
                      onClick={handleHashClick}
                      className={`flex items-center gap-2.5 min-h-11 px-3 rounded-xl text-sm font-medium transition-colors ${item.isActive
                        ? 'bg-primary dark:bg-primary-light text-white shadow-sm'
                        : 'text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800/90'
                        }`}
                    >
                      <i className={`fas ${item.icon} w-4 text-center text-[13px]`}></i>
                      <span>{item.label}</span>
                    </HashLink>
                  )}
                  {item.type === "dropdown" && (
                    <div>
                      <button
                        type="button"
                        aria-expanded={item.open}
                        onClick={() => toggleDropdown(item.dropdownKey)}
                        className={`w-full flex items-center justify-between min-h-11 px-3 rounded-xl text-sm font-medium transition-colors ${item.isActive
                          ? 'bg-primary dark:bg-primary-light text-white shadow-sm'
                          : 'text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800/90'
                          }`}
                      >
                        <div className="flex items-center gap-2.5">
                          <i className={`fas ${item.icon} w-4 text-center text-[13px]`}></i>
                          <span>{item.label}</span>
                        </div>
                        <i className={`fas fa-chevron-${item.open ? "up" : "down"} text-[10px] opacity-80`}></i>
                      </button>
                      {item.open && (
                        <div className="mt-1 ml-2 pl-2 border-l-2 border-slate-200 dark:border-slate-700 space-y-0.5">
                          {item.items.filter(subItem => subItem.show !== false).map((subItem, subIdx) => (
                            <div key={subIdx}>
                              {subItem.type === "link" && (
                                <Link
                                  to={subItem.to}
                                  onClick={() => {
                                    setMobileMenuOpen(false);
                                    if (subItem.onClick) subItem.onClick();
                                  }}
                                  className={`flex items-center gap-2.5 min-h-10 px-3 rounded-lg text-sm transition-colors ${subItem.isActive
                                    ? 'bg-primary/10 dark:bg-primary-light/10 text-primary dark:text-primary-light font-semibold'
                                    : 'text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800/80'
                                    }`}
                                >
                                  <i className={`fas ${subItem.icon} w-4 text-center text-[12px] text-slate-400`}></i>
                                  <span>{subItem.label}</span>
                                </Link>
                              )}
                              {subItem.type === "hashlink" && (
                                <HashLink
                                  to={subItem.to}
                                  onClick={(e) => {
                                    setDropdowns(prev => ({
                                      ...prev,
                                      [item.dropdownKey]: false
                                    }));
                                    setMobileMenuOpen(false);
                                    if (subItem.onClick) {
                                      subItem.onClick(e);
                                    }
                                  }}
                                  className={`flex items-center gap-2.5 min-h-10 px-3 rounded-lg text-sm transition-colors ${subItem.isActive
                                    ? 'bg-primary/10 dark:bg-primary-light/10 text-primary dark:text-primary-light font-semibold'
                                    : 'text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800/80'
                                    }`}
                                >
                                  <i className={`fas ${subItem.icon} w-4 text-center text-[12px] text-slate-400`}></i>
                                  <span>{subItem.label}</span>
                                </HashLink>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </nav>

            {/* Mobile Controls */}
            <div className="mt-4 pt-4 border-t border-slate-200 dark:border-slate-700 flex flex-wrap items-center gap-3">
              <div
                className="flex items-center gap-0.5 p-1 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800"
                role="group"
                aria-label={t("header.language", "Sprache")}
              >
                {[
                  { code: "de", src: "/images/flags/DE.png", label: "Deutsch" },
                  { code: "en", src: "/images/flags/GB.png", label: "English" },
                  { code: "zh", src: "/images/flags/CN.png", label: "中文" }
                ].map(({ code, src, label }) => (
                  <button
                    key={code}
                    type="button"
                    onClick={() => changeLanguage(code)}
                    aria-pressed={lang === code}
                    aria-label={label}
                    title={label}
                    className={`rounded-lg p-0.5 transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/30 ${
                      lang === code
                        ? "ring-2 ring-primary/40 bg-white dark:bg-slate-700 shadow-sm"
                        : "opacity-60 hover:opacity-100"
                    }`}
                  >
                    <img src={src} alt="" className="w-6 h-6 object-cover rounded-full" />
                  </button>
                ))}
              </div>
              <button
                type="button"
                onClick={toggleDarkMode}
                aria-label={darkMode ? t("header.themeLight", "Heller Modus") : t("header.themeDark", "Dunkler Modus")}
                title={darkMode ? t("header.themeLight", "Heller Modus") : t("header.themeDark", "Dunkler Modus")}
                className="relative h-8 w-11 shrink-0 rounded-full border border-slate-200 dark:border-slate-600 bg-slate-200 dark:bg-slate-700 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/25"
              >
                <span
                  className={`absolute top-1 left-1 flex h-6 w-6 items-center justify-center rounded-full bg-white dark:bg-slate-800 shadow-sm border border-slate-200 dark:border-slate-600 transition-transform duration-200 ${
                    darkMode ? "translate-x-3" : "translate-x-0"
                  }`}
                >
                  <i
                    className={`fas text-[10px] ${darkMode ? "fa-moon text-slate-500" : "fa-sun text-amber-500"}`}
                  ></i>
                </span>
              </button>
              {!isAuthenticated && (
                <HashLink
                  to="/sponsors#private-donation"
                  onClick={(e) => {
                    handleHashClick(e);
                    setMobileMenuOpen(false);
                  }}
                  className="flex items-center gap-2 h-10 px-4 rounded-xl bg-primary dark:bg-primary-light text-white text-sm font-semibold transition-all hover:bg-primary-dark dark:hover:bg-primary shadow-sm ml-auto"
                >
                  <i className="fas fa-heart text-[11px]"></i>
                  <span>{t("header.donate", "Spenden")}</span>
                </HashLink>
              )}
              {isAuthenticated && (
                <button
                  type="button"
                  onClick={() => {
                    setMobileMenuOpen(false);
                    setLogoutConfirmOpen(true);
                  }}
                  className="flex items-center gap-2 h-10 px-4 rounded-xl bg-slate-900 dark:bg-slate-800 text-white text-sm font-semibold transition-colors hover:bg-slate-800 dark:hover:bg-slate-700 ml-auto"
                >
                  <i className="fas fa-sign-out-alt text-xs"></i>
                  <span className="truncate max-w-[10rem]">{user?.username || t("auth.user", "Nutzer")}</span>
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      <ConfirmationModal
        isOpen={logoutConfirmOpen}
        onClose={() => setLogoutConfirmOpen(false)}
        onConfirm={handleLogout}
        title={t("header.logoutConfirm.title", "Abmelden")}
        subtitle={t("header.logoutConfirm.subtitle", "Möchtest du dich wirklich abmelden?")}
        confirmText={t("header.logoutConfirm.confirm", "Abmelden")}
        cancelText={t("header.logoutConfirm.cancel", "Abbrechen")}
        type="danger"
        layout="inline"
      />

      {/* Navigation-Lock Toast */}
      {navLockToast && (
        <div className="fixed top-20 left-1/2 -translate-x-1/2 z-[200] animate-fade-in-down">
          <div className="flex items-center gap-3 px-5 py-3 bg-amber-50 dark:bg-amber-900/90 border border-amber-300 dark:border-amber-700 rounded-xl shadow-lg shadow-amber-500/10">
            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-amber-100 dark:bg-amber-800 flex items-center justify-center">
              <i className="fas fa-lock text-amber-600 dark:text-amber-400 text-sm"></i>
            </div>
            <div>
              <p className="text-sm font-medium text-amber-800 dark:text-amber-200">
                Navigation gesperrt
              </p>
              <p className="text-xs text-amber-600 dark:text-amber-400">
                {lockMessage || 'Bitte starten Sie zuerst die Aufgabe.'}
              </p>
            </div>
            <button
              onClick={() => setNavLockToast(false)}
              className="flex-shrink-0 ml-2 text-amber-500 dark:text-amber-400 hover:text-amber-700 dark:hover:text-amber-300"
            >
              <i className="fas fa-times text-xs"></i>
            </button>
          </div>
        </div>
      )}
    </header>
  );
}

export default HeaderTailwind;
